import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import { User } from "../types/api";
import { validatePassword, type PasswordPolicy } from "../utils/passwordValidation";
import { translateApiError } from "../i18n/apiError";

export const ProfileSettings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["profileSettings", "common"]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [totpToken, setTotpToken] = useState("");
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadPasswordPolicy = async () => {
    try {
      const policy = await apiService.getPasswordPolicy();
      setPasswordPolicy(policy);
    } catch (err) {
      console.error("Failed to fetch password policy:", err);
      // Fallback to relaxed policy for development (matches backend dev config)
      setPasswordPolicy({
        min_length: 3,
        require_uppercase: false,
        require_lowercase: false,
        require_digit: false,
        require_special_char: false,
        special_chars: "!@#$%^&*(),.?\":{}|<>_-+=[]\\/~`",
        forbidden_patterns: [],
      });
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profile = await apiService.getProfile();
      setUser(profile.user);
    } catch (err) {
      setError(translateApiError(t, err));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordChangeSuccess(false);

    if (!passwordPolicy) {
      setPasswordError(t("errors.policyNotLoaded"));
      return;
    }

    // Validate using fetched password policy
    const policyErrors = validatePassword(newPassword, passwordPolicy);
    if (policyErrors.length > 0) {
      setPasswordError(policyErrors[0]);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t("errors.passwordMismatch"));
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError(t("errors.samePassword"));
      return;
    }

    if (user?.two_fa_enabled && totpToken.length !== 6) {
      setPasswordError(t("errors.twoFactorRequired"));
      return;
    }

    setPasswordChanging(true);

    try {
      await apiService.changePassword(
        currentPassword,
        newPassword,
        user?.two_fa_enabled ? totpToken : undefined,
      );

      setPasswordChangeSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTotpToken("");

      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordChangeSuccess(false);
      }, 3000);
    } catch (err) {
      setPasswordError(translateApiError(t, err));
    } finally {
      setPasswordChanging(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div style={{ maxWidth: "800px", margin: "40px auto", padding: "20px" }}>
        <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
          {t("loadingProfile")}
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={{ maxWidth: "800px", margin: "40px auto", padding: "20px" }}>
        <div
          style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "16px",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          {error || t("loadFailed")}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "800px", margin: "40px auto", padding: "20px" }}>
      <h1 style={{ marginTop: 0, marginBottom: "24px" }}>{t("title")}</h1>

      {/* Profile Information */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "24px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{
            marginTop: 0,
            marginBottom: "20px",
            borderBottom: "2px solid #e9ecef",
            paddingBottom: "12px",
          }}
        >
          {t("profileInfo.heading")}
        </h2>

        <div style={{ display: "grid", gap: "16px" }}>
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                color: "#495057",
                marginBottom: "4px",
              }}
            >
              {t("common:fields.name")}
            </label>
            <div
              style={{
                padding: "10px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
              }}
            >
              {user.name}
            </div>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                color: "#495057",
                marginBottom: "4px",
              }}
            >
              {t("common:fields.email")}
            </label>
            <div
              style={{
                padding: "10px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
              }}
            >
              {user.email}
            </div>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                color: "#495057",
                marginBottom: "4px",
              }}
            >
              {t("profileInfo.institution")}
            </label>
            <div
              style={{
                padding: "10px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
              }}
            >
              {user.institution}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  color: "#495057",
                  marginBottom: "4px",
                }}
              >
                {t("profileInfo.accountStatus")}
              </label>
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    padding: "4px 12px",
                    backgroundColor: user.is_active ? "#d4edda" : "#f8d7da",
                    color: user.is_active ? "#155724" : "#721c24",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  {user.is_active
                    ? t("profileInfo.active")
                    : t("profileInfo.inactive")}
                </span>
              </div>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  color: "#495057",
                  marginBottom: "4px",
                }}
              >
                {t("profileInfo.accountCreated")}
              </label>
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                {formatDate(user.created_at)}
              </div>
            </div>
          </div>

          {user.role === "org_admin" && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "4px",
              }}
            >
              <strong style={{ color: "#856404" }}>
                {t("roleBanner.orgAdmin.title")}
              </strong>
              <div
                style={{ fontSize: "13px", color: "#856404", marginTop: "4px" }}
              >
                {t("roleBanner.orgAdmin.description")}
              </div>
            </div>
          )}
          {user.role === "pi" && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#d1ecf1",
                border: "1px solid #17a2b8",
                borderRadius: "4px",
              }}
            >
              <strong style={{ color: "#0c5460" }}>
                {t("roleBanner.pi.title")}
              </strong>
              <div
                style={{ fontSize: "13px", color: "#0c5460", marginTop: "4px" }}
              >
                {t("roleBanner.pi.description")}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Security Settings */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "24px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            marginTop: 0,
            marginBottom: "20px",
            borderBottom: "2px solid #e9ecef",
            paddingBottom: "12px",
          }}
        >
          {t("security.heading")}
        </h2>

        {!showPasswordChange ? (
          <div>
            <button
              onClick={() => {
                setShowPasswordChange(true);
                loadPasswordPolicy();
              }}
              style={{
                padding: "12px 24px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {t("security.changePassword")}
            </button>
            <p
              style={{ fontSize: "13px", color: "#6c757d", marginTop: "12px" }}
            >
              {t("security.changePasswordHint")}
            </p>
          </div>
        ) : (
          <div>
            <h3 style={{ marginTop: 0, marginBottom: "16px" }}>
              {t("security.changePassword")}
            </h3>

            {passwordError && (
              <div
                style={{
                  backgroundColor: "#f8d7da",
                  color: "#721c24",
                  padding: "12px",
                  borderRadius: "4px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                {passwordError}
              </div>
            )}

            {passwordChangeSuccess && (
              <div
                style={{
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  padding: "12px",
                  borderRadius: "4px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                {t("security.successMessage")}
              </div>
            )}

            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="currentPassword"
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "6px",
                  }}
                >
                  {t("security.currentPasswordLabel")}
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="newPassword"
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "6px",
                  }}
                >
                  {t("security.newPasswordLabel")}
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
                {passwordPolicy && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginTop: "4px",
                    }}
                  >
                    {t("security.passwordRequirement.minLength", {
                      minLength: passwordPolicy.min_length,
                    })}
                    {passwordPolicy.require_uppercase &&
                      t("security.passwordRequirement.uppercase")}
                    {passwordPolicy.require_lowercase &&
                      t("security.passwordRequirement.lowercase")}
                    {passwordPolicy.require_digit &&
                      t("security.passwordRequirement.digit")}
                    {passwordPolicy.require_special_char &&
                      t("security.passwordRequirement.specialChar")}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="confirmPassword"
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "6px",
                  }}
                >
                  {t("security.confirmPasswordLabel")}
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {user.two_fa_enabled && (
                <div style={{ marginBottom: "16px" }}>
                  <label
                    htmlFor="totpToken"
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "6px",
                    }}
                  >
                    {t("security.twoFactorLabel")}
                  </label>
                  <input
                    type="text"
                    id="totpToken"
                    value={totpToken}
                    onChange={(e) =>
                      setTotpToken(
                        e.target.value.replace(/\D/g, "").slice(0, 6),
                      )
                    }
                    required
                    maxLength={6}
                    placeholder={t("security.twoFactorPlaceholder")}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "16px",
                      letterSpacing: "0.3em",
                      textAlign: "center",
                      fontFamily: "monospace",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginTop: "4px",
                    }}
                  >
                    {t("security.twoFactorHint")}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  type="submit"
                  disabled={passwordChanging}
                  style={{
                    padding: "10px 24px",
                    backgroundColor: passwordChanging ? "#6c757d" : "#28a745",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: passwordChanging ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {passwordChanging
                    ? t("security.changing")
                    : t("security.changePassword")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setTotpToken("");
                    setPasswordError(null);
                    setPasswordChangeSuccess(false);
                  }}
                  disabled={passwordChanging}
                  style={{
                    padding: "10px 24px",
                    backgroundColor: "transparent",
                    color: "#6c757d",
                    border: "1px solid #6c757d",
                    borderRadius: "4px",
                    cursor: passwordChanging ? "not-allowed" : "pointer",
                    fontSize: "14px",
                  }}
                >
                  {t("common:actions.cancel")}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
